/**
 * @fileoverview The Plants pop-up (D78 P1, mock-up frame 1): every "Add a plant" opens "Add a plant" with two rows,
 * "Take or upload a photo" (the owner's words: "Of plant(s) or of seed packet(s). Grow separates, names them and you
 * save them.") and "Type it in" ("Plant, type and variety. Not planted yet is fine."), which opens today's add form
 * unchanged (PlantFormDialog: search, Plant, Type with "Not sure", Variety, the bed, "Planted yet?").
 *
 * Opened from a bed (its "Add a new plant here", decision 6), the bed is preset: the form opens with it chosen, and
 * the photos carry it, so each card's bed is that bed.
 *
 * "Type it in" either opens the caller's own form (onType: the Plants page, whose one form also edits) or, given
 * onSave, the form this hook draws (Seasons, Beds).
 *
 * Where: PlantGrid (the add placeholders, ?add=1), SeasonPage (day one), LocationGrid (a bed's "Add a new plant
 * here").
 * API: the photo path's (usePhotoAdd); the form's onSave is the caller's.
 * Words (en/es): plants.form.addTitle (the pop-up's title), add.plant.photoHint, add.plant.photoHintNoAi,
 * add.plant.typeHint.
 *
 * @module useAddPlant
 */

import React from 'react';
import { PlantFormDialog } from '@/features/plant-list/components/dialogs/PlantFormDialog';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import type { Filed } from '@/features/photo-tray/components/TrayPiles';
import { AddChooser, type AddWaysWords } from './AddWays';
import { usePhotoAdd } from './usePhotoAdd';

/** The Plants pop-up's words under its rows. */
export const ADD_PLANT_WORDS: AddWaysWords = {
  photoHint: 'add.plant.photoHint',
  photoHintNoAi: 'add.plant.photoHintNoAi',
  typeHint: 'add.plant.typeHint',
};

/** What the pop-up is told. */
interface AddPlantOptions {
  /** "Type it in" opens the caller's own form, with the bed preset (or null). */
  onType?: (locationId: number | null) => void;
  /** Without onType: the form this hook draws saves through this (the caller's add). */
  onSave?: (payload: PlantWritePayload) => Promise<number | null>;
  /** Told of every filing from photos. */
  onFiled?: (filed: Filed) => void;
}

/** What the hook hands back. */
export interface AddPlantControl {
  /** Opens the pop-up, with a bed preset or none. */
  open: (locationId?: number | null) => void;
  /** Starts the photo row straight away (an empty garden's row in place). */
  photo: () => void;
  /** Opens "Type it in" straight away (the empty garden's row in place). */
  type: () => void;
  /** The pop-up, the photo path and the form: render it once. */
  element: React.ReactNode;
}

/**
 * The Plants pop-up and both its ways.
 *
 * @param options - AddPlantOptions.
 * @returns AddPlantControl.
 */
export function useAddPlant({ onType, onSave, onFiled }: AddPlantOptions): AddPlantControl {
  const { reactTranslate: t } = useTranslation();
  const [choosing, setChoosing] = React.useState(false);
  /** The bed it was opened from, or null. */
  const [bed, setBed] = React.useState<number | null>(null);
  /** The form this hook draws is open (no onType). */
  const [typing, setTyping] = React.useState(false);
  const photoAdd = usePhotoAdd({ locationId: bed, onFiled });

  /**
   * "Type it in": the caller's form, or this one.
   *
   * @param at - The bed to preset.
   */
  const type = (at: number | null = bed): void => {
    setChoosing(false);
    if (onType) onType(at); else setTyping(true);
  };
  /** "Take or upload a photo". */
  const photo = (): void => {
    setChoosing(false);
    photoAdd.start();
  };

  const element = (
    <>
      <AddChooser
        open={choosing}
        onOpenChange={setChoosing}
        title={t('plants.form.addTitle')}
        {...ADD_PLANT_WORDS}
        onPhoto={photo}
        onType={() => type()}
      />
      {photoAdd.element}
      {!onType && onSave && (
        <PlantFormDialog plant={null} isOpen={typing} onClose={() => setTyping(false)} onSave={onSave} initialLocationId={bed} />
      )}
    </>
  );

  return {
    open: (locationId = null) => { setBed(locationId); setChoosing(true); },
    photo: () => { setBed(null); photo(); },
    type: () => { setBed(null); type(null); },
    element,
  };
}
